import axios from 'axios';
import { isAuthenticated, getAccessToken } from '../features/auth/services/auth';
import { ContactPayload, ContactService } from '../types/types';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const getAuthHeaders = async () => {
    const isAuth = await isAuthenticated();
    if (!isAuth) throw new Error('User is not authenticated');
    const token = getAccessToken();
    return {
        Authorization: `Bearer ${token}`,
        'ngrok-skip-browser-warning': '69420'
    };
};
function buildQueryParams(params: Record<string, any>): string {
    const query = new URLSearchParams();
    for (const key in params) {
        const value = params[key];

        if (
            value === undefined ||
            value === null ||
            (typeof value === 'string' && value.trim() === '') ||
            (Array.isArray(value) && value.length === 0)
        ) {
            continue; // Skip empty values
        }

        if (Array.isArray(value)) {
            query.append(key, value.join(','));
        } else {
            query.append(key, value.toString());
        }
    }

    return query.toString();
}

export const contactService: ContactService = {

    getPresignedUrl: async (fileName: string, fileType: string) => {
        const headers = await getAuthHeaders();
        const response = await axios.post(`${API_BASE_URL}/portal/contacts/presigned-url`, {
            fileName,
            fileType
        }, { headers });
        return response.data;
    },

    uploadImage: async (url: string, file: File) => {
        await axios.put(url, file, {
            headers: { 'Content-Type': file.type }
        });
    },

    createContact: async (contactData: any) => {
        const headers = await getAuthHeaders();
        const response = await axios.post(`${API_BASE_URL}/portal/contacts`, contactData, { headers });
        return response.data;
    },

    updateContact: async (id: number, contactData: any) => {
        const headers = await getAuthHeaders();
        const response = await axios.put(`${API_BASE_URL}/portal/contacts/${id}`, contactData, { headers });
        return response.data;
    },

    getOrganizations: async () => {
        const headers = await getAuthHeaders();
        const response = await axios.get(`${API_BASE_URL}/portal/organizations/minimal`, { headers });
        return response.data;
    },

    getContactRoles: async () => {
        const headers = await getAuthHeaders();
        const response = await axios.get(`${API_BASE_URL}/portal/contact-roles`, { headers });
        return response.data;
    },

    getContacts: async (
        page: number,
        perPage: number,
        sort?: { field: string; direction: 'ASC' | 'DESC' },
        query?: string,
        filters: Record<string, any> = {},
        visibleColumns: string[] = []
    ) => {
        const headers = await getAuthHeaders();
        const params: Record<string, any> = {
            page,
            perPage,
            query,
            ...filters,
            listingColumns: visibleColumns.length > 0 ? visibleColumns : undefined,
        };

        if (sort) {
            if (sort.field === 'name') {
                params.sort = 'firstName';
                params.sortDir = sort.direction;

            } else {
                params.sort = sort.field;
                params.sortDir = sort.direction;
            }
        }
        const queryString = buildQueryParams(params);
        const response = await axios.get(`${API_BASE_URL}/portal/contacts?${queryString}`, {
            headers,
        });

        return response.data;
    },

    getContactById: async (id: number) => {
        const headers = await getAuthHeaders();
        const response = await axios.get(`${API_BASE_URL}/portal/contacts/${id}`, { headers });
        return response.data;
    },

    deleteContact: async (id: string) => {
        const headers = await getAuthHeaders();
        try {
            const response = await axios.delete(`${API_BASE_URL}/portal/contacts/${id}`, { headers });
            return response.data;
        } catch (error) {
            throw new Error('Failed to delete contact');
        }
    },
    getEntityById: async (id: number) => {
        const response = await axios.get(`${API_BASE_URL}/portal/contacts/${id}`, { headers: await getAuthHeaders() });
        return response.data;
    },

    createEntity: async (contactData: ContactPayload) => {
        const response = await axios.post(`${API_BASE_URL}/portal/contacts`, contactData, { headers: await getAuthHeaders() });
        return response.data;
    },

    updateEntity: async (id: number, contactData: ContactPayload) => {
        const response = await axios.put(`${API_BASE_URL}/portal/contacts/${id}`, contactData, { headers: await getAuthHeaders() });
        return response.data;
    },
    getDynamicColumns: async (scope: string, query: string = "", page: number = 1, perPage: number = 50) => {
        const headers = await getAuthHeaders();
        const params: Record<string, any> = {
            page,
            perPage,
            query
        };
        const response = await axios.get(`${API_BASE_URL}/portal/search-query/columns?scope=${scope}`, { headers, params });
        return response.data;
    },
    getDynamicFilters: async (scope: string) => {
        const headers = await getAuthHeaders();
        const response = await axios.get(`${API_BASE_URL}/portal/search-query/filters?scope=${scope}`, { headers });
        return response.data;
    },

    // Portal access / Cognito-related actions
    grantPortalAccess: async (id: number) => {
        const headers = await getAuthHeaders();
        const response = await axios.post(`${API_BASE_URL}/portal/contacts/${id}/portal-access`, {}, { headers });
        return response.data;
    },

    suspendPortalAccess: async (id: number) => {
        const headers = await getAuthHeaders();
        const response = await axios.post(`${API_BASE_URL}/portal/contacts/${id}/portal-access/suspend`, {}, { headers });
        return response.data;
    },

    reactivatePortalAccess: async (id: number) => {
        const headers = await getAuthHeaders();
        const response = await axios.post(`${API_BASE_URL}/portal/contacts/${id}/portal-access/reactivate`, {}, { headers });
        return response.data;
    },

    resendPortalInvite: async (id: number) => {
        const headers = await getAuthHeaders();
        const response = await axios.post(`${API_BASE_URL}/portal/contacts/${id}/portal-access/resend-invite`, {}, { headers });
        return response.data;
    },

    resetPortalPassword: async (id: number) => {
        const headers = await getAuthHeaders();
        const response = await axios.post(`${API_BASE_URL}/portal/contacts/${id}/portal-access/reset-password`, {}, { headers });
        return response.data;
    },

    getPortalAccessStatus: async (id: number) => {
        const headers = await getAuthHeaders();
        const response = await axios.get(`${API_BASE_URL}/portal/contacts/${id}/portal-access`, { headers });
        return response.data;
    }
};